import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthProvider, useNotify } from 'react-admin';
import { Identity } from '../../../interfaces/auth';
import { RpmExportedFilters } from '../../../interfaces/billing';
import { BillingClaim, CsvRawContent } from '../../../interfaces/billing.rpm';
import { IClinic } from '../../../interfaces/clinic';
import { SelectOption } from '../../../interfaces/common';
import { PayerItem } from '../../../interfaces/eligibility';
import { BillingService } from '../../../services';
import coreService from '../../../services/core';
import eligibilityService from '../../../services/eligibility';
import { MANKATO_CLINIC_SLUG } from '../../../util/constants';
import { formatDateStringToYYYYMMDD } from '../../../util/dateFormat';
import { INVALID_STORAGE_ERROR } from '../common/constants';
import { RPMFilters } from '../common/RPMFilters';
import {
  FilterConfiguration,
  Filters,
  INITIAL_PAGINATION,
  LoadingStates,
  RPMCptCodes,
  RpmStatus,
} from '../types/rpms.types';
import { RPMExportResultsTable } from './RPMExportResultsTable';

type RPMExportResultsProps = {
  processId?: number;
  clinics: IClinic[];
};

type ClinicOption = SelectOption & { slug?: string };

const filterConfigurations = [
  {
    key: 'clinic',
    label: 'Clinic',
    options: [],
    section: 'RPM Export Results',
    type: 'select',
  },
  {
    key: 'insuranceCompany',
    label: 'Insurance Company',
    options: [],
    section: 'RPM Export Results',
    type: 'autocomplete',
    fetchOptions: async (searchTerm: string) => {
      try {
        const response = await eligibilityService.getPayers({
          filter: searchTerm || null,
        });
        const options =
          response?.data?.map((payer: PayerItem) => ({
            label: payer.name,
            value: payer.id,
          })) || [];
        return options;
      } catch (error) {
        console.error('Error fetching insurance companies:', error);
        return [];
      }
    },
  },
  {
    key: 'dateOfService',
    label: 'Date Of Service',
    type: 'date',
  },
  {
    key: 'cptCodes',
    label: 'CPT Codes',
    options: Object.values(RPMCptCodes).map((value) => ({ value, label: value })),
    section: 'Patient Selection',
    type: 'select',
  },
  {
    key: 'memberUuids',
    label: 'Member Search',
    options: [],
    section: 'RPM Export Results',
    type: 'autocomplete',
    fetchOptions: async (searchTerm: string) => {
      try {
        if (!searchTerm) {
          return [];
        }

        const response = await coreService.fetchMembers({
          searchTerm,
        });
        const options =
          response?.data?.map((member) => ({
            label: member.fullName,
            value: member.uuid,
          })) || [];
        return options;
      } catch (error) {
        console.error('Error fetching members:', error);
        return [];
      }
    },
  },
  {
    key: 'claimStatus',
    label: 'Claim Status',
    options: [
      { value: 'VALID', label: 'Valid' },
      { value: 'INVALID', label: 'Invalid' },
    ],
    section: 'Claim Status',
    type: 'select',
  },
] as FilterConfiguration<Filters>[];

const initialFilters: Filters = {
  clinic: [],
  insuranceCompany: [],
  dateOfService: [],
  cptCodes: [],
  memberUuids: [],
  claimStatus: [],
};

export const RPMExportResults: React.FC<RPMExportResultsProps> = ({ processId, clinics }) => {
  const [data, setData] = useState<BillingClaim[]>([]);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [selectedClaims, setSelectedClaims] = useState<number[]>([]);
  const [allRecordsSelected, setAllRecordsSelected] = useState(false);
  const [excludedRows, setExcludedRows] = useState<{ id: number; index: number }[]>([]);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    isApplying: false,
    isClearing: false,
  });

  const authProvider = useAuthProvider();
  const notify = useNotify();

  const clinicOptions: ClinicOption[] = useMemo(
    () =>
      clinics.map((clinic) => ({ label: clinic.name, value: `${clinic.id}`, slug: clinic.slug })),
    [clinics],
  );

  const buildExportParameters = useCallback(
    (currentFilters: Filters, page: number, limit: number): RpmExportedFilters => ({
      clinicIds: currentFilters.clinic.map((id) => parseInt(id)),
      insuranceCompanyIds: currentFilters.insuranceCompany,
      cptCodes: currentFilters.cptCodes,
      dateOfService: currentFilters.dateOfService?.[0]
        ? formatDateStringToYYYYMMDD(currentFilters.dateOfService?.[0])
        : '',
      memberUuids: currentFilters.memberUuids,
      claimStatus: currentFilters.claimStatus,
      processId,
      page,
      limit,
    }),
    [processId],
  );

  const applyClaimStatusFilter = (data: BillingClaim[], statusFilters: string[]) => {
    if (!statusFilters.length) return data;

    return data.filter((claim) => {
      const displayStatus = claim.status === RpmStatus.EXPORTED ? 'VALID' : 'INVALID';
      return statusFilters.includes(displayStatus);
    });
  };

  const fetchExportedClaims = useCallback(async (parameters: RpmExportedFilters) => {
    setLoadingStates((prev) => ({ ...prev, isApplying: true }));

    try {
      // Extract claimStatus for client-side filtering
      const { claimStatus, ...serverParameters } = parameters;

      const response = await new BillingService().getExportedClaims(serverParameters);
      const rawData = response?.data || [];

      // Apply client-side filtering for claim status
      const filteredData = applyClaimStatusFilter(rawData, claimStatus || []);

      setData(filteredData);

      setPagination(response?.pagination || INITIAL_PAGINATION);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, isApplying: false }));
    }
  }, []);

  useEffect(() => {
    const parameters = buildExportParameters(filters, pagination.currentPage, pagination.limit);
    fetchExportedClaims(parameters);
  }, [
    buildExportParameters,
    fetchExportedClaims,
    filters,
    pagination.currentPage,
    pagination.limit,
    processId,
  ]);

  const handleApplyFilters = async (appliedFilters: Filters) => {
    setLoadingStates((prev) => ({ ...prev, isApplying: true }));
    setFilters(appliedFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    const parameters = buildExportParameters(appliedFilters, 1, pagination.limit);
    await fetchExportedClaims(parameters);
  };

  const handleClearFilters = async () => {
    setLoadingStates((prev) => ({ ...prev, isClearing: true }));
    setFilters(initialFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    const parameters = buildExportParameters(initialFilters, 1, pagination.limit);
    await fetchExportedClaims(parameters);

    setSelectedClaims([]);
    setLoadingStates((prev) => ({ ...prev, isClearing: false }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      limit: newLimit,
      currentPage: 1,
    }));
  };

  const updatedFilterConfigurations = filterConfigurations.map((config) =>
    config.key === 'clinic' ? { ...config, options: clinicOptions } : config,
  );

  const handleExportNotes = async () => {
    setLoadingStates((prev) => ({ ...prev, isProcessing: true }));

    const billingServiceClient = new BillingService();
    const identity = (await authProvider.getIdentity!()) as Identity;
    if (!identity?.uuid || !identity?.fullName) {
      notify(INVALID_STORAGE_ERROR, 'error');
      setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
      return;
    }

    let select: number[] | null = null;
    let exclude: number[] | null = null;
    if (allRecordsSelected && excludedRows.length) {
      exclude = excludedRows.map(({ id }) => id);
    } else if (!allRecordsSelected && selectedClaims.length) {
      select = selectedClaims.filter((claimId): claimId is number => claimId !== undefined);
    }

    const result = await billingServiceClient.exportNotes(
      {
        ...(select && { select }),
        ...(exclude && { exclude }),
      },
      {
        processId,
        ...(filters.clinic?.length && {
          clinicIds: filters.clinic.map((cId) => Number.parseInt(cId)),
        }),
        ...(filters.insuranceCompany?.length && { insuranceCompanyIds: filters.insuranceCompany }),
        ...(filters.dateOfService?.length && {
          dateOfService: formatDateStringToYYYYMMDD(filters.dateOfService[0] as unknown as Date),
        }),
        ...(filters.cptCodes?.length && { cptCodes: filters.cptCodes }),
        ...(filters.memberUuids?.length && { memberUuids: filters.memberUuids }),
        // Note: claimStatus is handled client-side, so we don't include it in the export parameters
      },
      {
        fullName: identity.fullName,
        uuid: identity.uuid,
      },
    );

    if (!result) {
      notify(`Can't export the Notes`, 'error', {
        autoHideDuration: 30,
      });
      setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
      return;
    }

    notify(`Export was successfully`, 'success', {
      autoHideDuration: 30,
    });

    setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
  };

  const handleExportFileForMankatoDownload = async () => {
    setLoadingStates((prev) => ({ ...prev, isProcessing: true }));

    const clinic = clinicOptions.find((c) => c.slug === MANKATO_CLINIC_SLUG);
    if (!clinic) {
      notify('Clinic not found for the given slug.', 'error');
      return;
    }

    const billingServiceClient = new BillingService();

    try {
      const csvData = await billingServiceClient.exportCSVFile(
        parseInt(clinic.value),
        processId ?? null,
      );

      if (!csvData || csvData.rows.length === 0) {
        notify(
          csvData ? `No data available for the current process` : `Can't export the Notes`,
          'info',
          { autoHideDuration: 30 },
        );
        return;
      }

      generateAndDownloadCSV(csvData, `export-${processId ?? 'unknown'}.csv`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      notify(`An error occurred while exporting`, 'error', { autoHideDuration: 30 });
    } finally {
      setLoadingStates((prev) => ({ ...prev, isProcessing: false }));
    }
  };

  const generateAndDownloadCSV = (csvData: CsvRawContent, filename: string = 'export.csv') => {
    const csvContent = [
      csvData.headers.join(','),
      ...csvData.rows.map((row) => row.map((value) => `"${value}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <RPMFilters<Filters>
        configurations={updatedFilterConfigurations}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        initialFilters={initialFilters}
        loadingStates={loadingStates}
        selectedCount={selectedClaims.length}
        additionalButtons={
          <>
            <Tooltip title='This button will only export data for Mankato Clinic'>
              <span>
                <Button
                  variant='contained'
                  onClick={handleExportFileForMankatoDownload}
                  disabled={data.length === 0 || loadingStates.isProcessing}
                  endIcon={
                    loadingStates.isProcessing ? (
                      <CircularProgress style={{ color: '#ffffff' }} size={20} />
                    ) : (
                      <SaveAltIcon />
                    )
                  }>
                  Export Mankato File
                </Button>
              </span>
            </Tooltip>
            <Button
              variant='contained'
              onClick={handleExportNotes}
              disabled={
                (!allRecordsSelected && selectedClaims.length === 0) || loadingStates.isProcessing
              }
              endIcon={
                loadingStates.isProcessing ? (
                  <CircularProgress style={{ color: '#ffffff' }} size={20} />
                ) : (
                  <NavigateNextIcon />
                )
              }>
              {allRecordsSelected ? 'Export all notes' : `Export ${selectedClaims.length} notes`}
            </Button>
          </>
        }
      />

      <RPMExportResultsTable
        onClaimIdsSelectionChange={setSelectedClaims}
        selectedClaimIds={selectedClaims}
        onAllRecordsSelected={setAllRecordsSelected}
        allRecordsSelected={allRecordsSelected}
        onRowExclusionChange={setExcludedRows}
        excludedRows={excludedRows}
        onRowsPerPageChange={handleChangeRowsPerPage}
        isLoading={loadingStates.isApplying}
        pagination={pagination}
        data={data}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};
